import {
  Abc,
  AccountTree,
  Category,
  Chat,
  Check,
  CheckBox,
  CheckBoxOutlineBlank,
  DatasetOutlined,
  FilterAlt,
  GridView,
  InsertDriveFile,
  Settings,
  Style,
  TableChart,
  ViewList,
} from '@mui/icons-material';
import {
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useModal } from 'components/modal';
import SubMenu from 'components/SubMenu';
import { plural } from 'pluralize';
import React, { useState } from 'react';
import { FinsightEntity } from 'shared';
import { ControllerState } from 'types/react';
import { formatName } from 'utils/fields';

import { ViewType } from '../finsightEntity.view';
import ConfigureDefaultsModal from './ConfigureDefaultsModal';

type EntityChildrenFilterProps = {
  childrenAll: ControllerState<boolean>;
  viewType: ControllerState<ViewType>;
  categorizeBy: ControllerState<string[]>;
  sortBy: ControllerState<string | null>;
  tab?: Omit<ControllerState<string | null>, 'onChange'> & {
    onChange: (v: string) => void;
  };
  childrenTypes?: string[];
  extraFieldNames?: string[];
  entity: FinsightEntity;
};

export default function EntityChildrenFilter(props: EntityChildrenFilterProps) {
  const {
    childrenAll,
    viewType,
    categorizeBy,
    sortBy,
    tab,
    childrenTypes = [],
    extraFieldNames = [],
    entity,
  } = props;

  const [anchor, setAnchorEl] = useState<null | HTMLElement>(null);
  const { showModal } = useModal();

  const getViewTypeLabel = (type: ViewType) => {
    switch (type) {
      case 'list':
        return 'List View';
      case 'card':
        return 'Card View';
      case 'table':
        return 'Table View';
      case 'text':
        return 'Text View';
      case 'spreadsheet':
        return 'Spreadsheet View';
      case 'discussion':
        return 'Discussion View';
      case 'files':
        return 'Files View';
      case 'flashcards':
        return 'Flashcards View';
      default:
        return 'List View';
    }
  };

  const getCategorizationLabel = (value: string[]) => {
    if (value.length === 0) return 'Default';
    if (value.length === 1) {
      if (value[0] === 'type') return 'Type';
      return formatName(value[0]);
    }
    return `${value.length} categories`;
  };

  const getSortByLabel = (value: string | null) => {
    if (!value) return 'Default';
    return value;
  };
  const getTabLabel = (value: string | null) => {
    if (!value) return 'All';
    if (value === 'all') return 'All';
    return plural(formatName(value));
  };

  return (
    <>
      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
        <FilterAlt />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{
          sx: {
            minWidth: 250,
          },
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          sx={{
            px: 1,
            width: '100%',
            '& > button': { flex: 1 },
          }}
          value={viewType.current}
          onChange={(_, v) => viewType.onChange(v)}
        >
          <Tooltip title="List View">
            <ToggleButton value="list">
              <ViewList />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Card View">
            <ToggleButton value="card">
              <GridView />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Table View">
            <ToggleButton value="table">
              <DatasetOutlined />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Spreadsheet View">
            <ToggleButton value="spreadsheet">
              <TableChart />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
        <Divider
          sx={{
            mt: 1,
          }}
        />
        <SubMenu
          buttonChildren={
            <ListItemText
              primary="View Type"
              secondary={getViewTypeLabel(viewType.current)}
            />
          }
        >
          <ListItemButton
            onClick={() => {
              viewType.onChange('list');
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              {viewType.current === 'list' ? <Check /> : <ViewList />}
            </ListItemIcon>
            <ListItemText primary="List View" />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              viewType.onChange('card');
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              {viewType.current === 'card' ? <Check /> : <GridView />}
            </ListItemIcon>
            <ListItemText primary="Card View" />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              viewType.onChange('table');
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              {viewType.current === 'table' ? <Check /> : <DatasetOutlined />}
            </ListItemIcon>
            <ListItemText primary="Table View" />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              viewType.onChange('text');
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              {viewType.current === 'text' ? <Check /> : <Abc />}
            </ListItemIcon>
            <ListItemText primary="Text View" />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              viewType.onChange('spreadsheet');
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              {viewType.current === 'spreadsheet' ? <Check /> : <TableChart />}
            </ListItemIcon>
            <ListItemText primary="Spreadsheet View" />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              viewType.onChange('discussion');
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              {viewType.current === 'discussion' ? <Check /> : <Chat />}
            </ListItemIcon>
            <ListItemText primary="Discussion View" />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              viewType.onChange('files');
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              {viewType.current === 'files' ? <Check /> : <InsertDriveFile />}
            </ListItemIcon>
            <ListItemText primary="Files View" />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              viewType.onChange('flashcards');
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              {viewType.current === 'flashcards' ? <Check /> : <Style />}
            </ListItemIcon>
            <ListItemText primary="Flashcards View" />
          </ListItemButton>
        </SubMenu>

        <SubMenu
          buttonChildren={
            <ListItemText
              primary="Categorize By"
              secondary={getCategorizationLabel(categorizeBy.current)}
            />
          }
        >
          <ListItemButton
            onClick={() => {
              categorizeBy.onChange([]);
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              {categorizeBy.current.length === 0 ? (
                <Check />
              ) : (
                <CheckBoxOutlineBlank />
              )}
            </ListItemIcon>
            <ListItemText primary="Default" />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              const current = categorizeBy.current;
              const isSelected = current.includes('type');
              if (isSelected) {
                categorizeBy.onChange(current.filter((v) => v !== 'type'));
              } else {
                categorizeBy.onChange([...current, 'type']);
              }
            }}
          >
            <ListItemIcon>
              {categorizeBy.current.includes('type') ? (
                <CheckBox />
              ) : (
                <CheckBoxOutlineBlank />
              )}
            </ListItemIcon>
            <ListItemText primary="Type" />
          </ListItemButton>
          {extraFieldNames.map((fieldName) => (
            <ListItemButton
              key={fieldName}
              onClick={() => {
                const current = categorizeBy.current;
                const isSelected = current.includes(fieldName);
                if (isSelected) {
                  categorizeBy.onChange(current.filter((v) => v !== fieldName));
                } else {
                  categorizeBy.onChange([...current, fieldName]);
                }
              }}
            >
              <ListItemIcon>
                {categorizeBy.current.includes(fieldName) ? (
                  <CheckBox />
                ) : (
                  <CheckBoxOutlineBlank />
                )}
              </ListItemIcon>
              <ListItemText primary={formatName(fieldName)} />
            </ListItemButton>
          ))}
        </SubMenu>

        <SubMenu
          buttonChildren={
            <ListItemText
              primary="Sort By"
              secondary={getSortByLabel(sortBy.current)}
            />
          }
        >
          <ListItemButton
            onClick={() => {
              sortBy.onChange(null);
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              {sortBy.current === null ? <Check /> : <Category />}
            </ListItemIcon>
            <ListItemText primary="Default" />
          </ListItemButton>
          {extraFieldNames.map((fieldName) => (
            <ListItemButton
              key={fieldName}
              onClick={() => {
                sortBy.onChange(fieldName);
                setAnchorEl(null);
              }}
            >
              <ListItemIcon>
                {sortBy.current === fieldName ? <Check /> : <Category />}
              </ListItemIcon>
              <ListItemText primary={formatName(fieldName)} />
            </ListItemButton>
          ))}
        </SubMenu>
        {tab && childrenTypes.length > 0 && (
          <SubMenu
            buttonChildren={
              <ListItemText
                primary="Children Type"
                secondary={getTabLabel(tab.current)}
              />
            }
          >
            {childrenTypes.map((type) => (
              <ListItemButton
                key={type}
                onClick={() => {
                  tab.onChange(type);
                  setAnchorEl(null);
                }}
              >
                <ListItemIcon>
                  {tab.current === type ? <Check /> : <Category />}
                </ListItemIcon>
                <ListItemText
                  primary={type === 'all' ? 'All' : plural(formatName(type))}
                />
              </ListItemButton>
            ))}
          </SubMenu>
        )}
        <SubMenu
          buttonChildren={
            <ListItemText
              primary="Children Filter"
              secondary={
                childrenAll.current ? 'All Children' : 'Immediate Children'
              }
            />
          }
        >
          <ListItemButton
            onClick={() => {
              childrenAll.onChange(true);
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              {childrenAll.current === true ? <Check /> : <AccountTree />}
            </ListItemIcon>
            <ListItemText primary="All Children" />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              childrenAll.onChange(false);
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              {childrenAll.current === false ? <Check /> : <AccountTree />}
            </ListItemIcon>
            <ListItemText primary="Immediate Children" />
          </ListItemButton>
        </SubMenu>
        <Divider />
        <ListItemButton
          onClick={() => {
            setAnchorEl(null);
            showModal(
              'Configure Defaults',
              <ConfigureDefaultsModal entity={entity} />,
              "Set the default view mode for this entity's children",
            );
          }}
        >
          <ListItemIcon>
            <Settings />
          </ListItemIcon>
          <ListItemText primary="Configure Defaults" />
        </ListItemButton>
      </Menu>
    </>
  );
}
